import { Search01Icon } from "~/components/icons/central-icons";
import { CentralIcon } from "~/components/icons/central-icons";
import type { ComponentProps, ReactNode } from "react";
import { StatusAlert } from "~/components/status-alert";
import { StatusPill } from "~/components/status-pill";
import { Badge } from "~/components/ui/badge";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
} from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "~/components/ui/input-group";
import {
  NativeSelect,
  NativeSelectOption,
} from "~/components/ui/native-select";
import { cn } from "~/lib/utils";

export interface TokenAssetSearchChainOption {
  label: string;
  secondaryLabel?: string | null;
  value: string;
}

type TokenAssetSearchBadge =
  | {
      kind: "badge";
      label: string;
      variant?: ComponentProps<typeof Badge>["variant"];
    }
  | {
      kind: "status";
      label: string;
      tone?: ComponentProps<typeof StatusPill>["tone"];
    };

export interface TokenAssetSearchResult {
  badges?: TokenAssetSearchBadge[];
  disabled?: boolean;
  key: string;
  onSelect: () => void;
  primaryLabel: string;
  secondaryLabel?: string | null;
  source?: string | null;
  tertiaryLabel?: string | null;
  testId?: string;
}

interface TokenAssetSearchPickerBaseProps {
  emptyMessage: string;
  errorMessage?: string | null;
  isLoading: boolean;
  loadingMessage: string;
  onSearchTermChange: (value: string) => void;
  resultLayout?: "compact" | "detailed";
  results: TokenAssetSearchResult[];
  resultsRole?: string;
  resultsLabel?: string;
  resultsVariant?: "panel" | "plain";
  searchDescription?: ReactNode;
  searchDisabled?: boolean;
  searchFieldVariant?: "group" | "plain";
  searchInputId: string;
  searchInputTestId?: string;
  searchLabel: string;
  searchPlaceholder: string;
  searchTerm: string;
  showResults?: boolean;
}

type TokenAssetSearchPickerChainProps =
  | {
      chainDescription?: ReactNode;
      chainId: string;
      chainLabel: string;
      chainOptions: TokenAssetSearchChainOption[];
      chainSelectDisabled?: boolean;
      chainSelectId: string;
      chainSelectTestId?: string;
      onChainIdChange: (chainId: string) => void;
      showChainFilter?: true;
    }
  | {
      showChainFilter: false;
    };

type TokenAssetSearchPickerProps = TokenAssetSearchPickerBaseProps &
  TokenAssetSearchPickerChainProps;

function hasVisibleChainFilter(
  props: TokenAssetSearchPickerProps,
): props is TokenAssetSearchPickerBaseProps &
  Extract<TokenAssetSearchPickerChainProps, { showChainFilter?: true }> {
  return props.showChainFilter !== false;
}

interface TokenAssetSearchInputProps {
  onSearchTermChange: (value: string) => void;
  searchDisabled?: boolean;
  searchFieldVariant?: "group" | "plain";
  searchInputId: string;
  searchInputTestId?: string;
  searchPlaceholder: string;
  searchTerm: string;
}

function renderChainFilter(props: TokenAssetSearchPickerProps) {
  if (!hasVisibleChainFilter(props)) {
    return null;
  }

  return (
    <Field>
      <FieldLabel htmlFor={props.chainSelectId}>{props.chainLabel}</FieldLabel>
      <FieldContent>
        <NativeSelect
          data-testid={props.chainSelectTestId}
          disabled={props.chainSelectDisabled}
          id={props.chainSelectId}
          onChange={(event) => {
            props.onChainIdChange(event.currentTarget.value);
          }}
          value={props.chainId}
        >
          {props.chainOptions.map((option) => (
            <NativeSelectOption key={option.value} value={option.value}>
              {option.secondaryLabel
                ? `${option.label} (${option.secondaryLabel})`
                : option.label}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        {props.chainDescription ? (
          <FieldDescription>{props.chainDescription}</FieldDescription>
        ) : null}
      </FieldContent>
    </Field>
  );
}

function renderTokenAssetSearchBadge(
  badge: TokenAssetSearchBadge,
  key: string,
) {
  if (badge.kind === "status") {
    return (
      <StatusPill key={key} tone={badge.tone ?? "default"}>
        {badge.label}
      </StatusPill>
    );
  }

  return (
    <Badge key={key} variant={badge.variant ?? "outline"}>
      {badge.label}
    </Badge>
  );
}

function renderSearchInput(props: TokenAssetSearchInputProps) {
  if (props.searchFieldVariant === "group") {
    return (
      <InputGroup className="h-9 bg-input/50">
        <InputGroupInput
          data-testid={props.searchInputTestId}
          disabled={props.searchDisabled}
          id={props.searchInputId}
          onChange={(event) => {
            props.onSearchTermChange(event.currentTarget.value);
          }}
          placeholder={props.searchPlaceholder}
          value={props.searchTerm}
        />
        <InputGroupAddon align="inline-end">
          <CentralIcon
            className="size-4 shrink-0 opacity-50"
            icon={Search01Icon}
          />
        </InputGroupAddon>
      </InputGroup>
    );
  }

  return (
    <Input
      data-testid={props.searchInputTestId}
      disabled={props.searchDisabled}
      id={props.searchInputId}
      onChange={(event) => {
        props.onSearchTermChange(event.currentTarget.value);
      }}
      placeholder={props.searchPlaceholder}
      value={props.searchTerm}
    />
  );
}

export function TokenAssetSearchPicker(props: TokenAssetSearchPickerProps) {
  const resultLayout = props.resultLayout ?? "detailed";
  const resultsVariant = props.resultsVariant ?? "panel";
  const showResults = props.showResults ?? true;

  return (
    <div className="space-y-4">
      {renderChainFilter(props)}

      <Field>
        <FieldLabel htmlFor={props.searchInputId}>
          {props.searchLabel}
        </FieldLabel>
        <FieldContent>
          {renderSearchInput(props)}
          {props.searchDescription ? (
            <FieldDescription>{props.searchDescription}</FieldDescription>
          ) : null}
        </FieldContent>
      </Field>

      {showResults ? (
        <div className="space-y-2">
          {props.resultsLabel ? (
            <div className="px-1 text-caption font-medium text-muted-foreground">
              {props.resultsLabel}
            </div>
          ) : null}
          <div
            className={cn(
              resultsVariant === "panel"
                ? "glider-panel-muted max-h-[26rem] overflow-y-auto rounded-[1.5rem] p-1"
                : "space-y-2",
            )}
          >
            {props.errorMessage ? (
              resultsVariant === "panel" ? (
                <StatusAlert
                  className="m-2"
                  description={props.errorMessage}
                  tone="destructive"
                />
              ) : (
                <p className="text-meta leading-6 text-status-negative-foreground">
                  {props.errorMessage}
                </p>
              )
            ) : props.isLoading ? (
              <div
                className={cn(
                  resultsVariant === "panel"
                    ? "px-3 py-8 text-center text-meta text-muted-foreground"
                    : "text-meta leading-6 text-muted-foreground",
                )}
              >
                {props.loadingMessage}
              </div>
            ) : props.results.length === 0 ? (
              <div
                className={cn(
                  resultsVariant === "panel"
                    ? "py-6 text-center text-meta text-muted-foreground"
                    : "text-meta leading-6 text-muted-foreground",
                )}
              >
                {props.emptyMessage}
              </div>
            ) : (
              <div
                className={cn(
                  "flex flex-col gap-1",
                  resultsVariant === "plain" && "gap-2",
                )}
                role={props.resultsRole}
              >
                {props.results.map((result) => (
                  <button
                    className={cn(
                      "w-full cursor-pointer rounded-2xl text-left transition-colors hover:bg-hover focus-visible:bg-hover glider-focus-ring disabled:cursor-not-allowed",
                      resultLayout === "detailed"
                        ? "flex items-start gap-3 px-3 py-2"
                        : "border border-border/70 px-3 py-2",
                    )}
                    data-source={result.source ?? undefined}
                    data-testid={result.testId}
                    disabled={result.disabled}
                    key={result.key}
                    onClick={() => {
                      result.onSelect();
                    }}
                    type="button"
                  >
                    {resultLayout === "detailed" ? (
                      <>
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="truncate text-meta font-medium">
                              {result.primaryLabel}
                            </span>
                            {result.secondaryLabel ? (
                              <span className="truncate text-caption text-muted-foreground">
                                {result.secondaryLabel}
                              </span>
                            ) : null}
                          </div>
                          {result.tertiaryLabel ? (
                            <span className="truncate text-caption text-muted-foreground">
                              {result.tertiaryLabel}
                            </span>
                          ) : null}
                        </div>
                        {result.badges && result.badges.length > 0 ? (
                          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                            {result.badges.map((badge, index) =>
                              renderTokenAssetSearchBadge(
                                badge,
                                `${result.key}:${badge.kind}:${badge.label}:${index}`,
                              ),
                            )}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <span className="block font-medium">
                          {result.primaryLabel}
                        </span>
                        {result.secondaryLabel ? (
                          <span className="block text-caption text-muted-foreground">
                            {result.secondaryLabel}
                          </span>
                        ) : null}
                      </>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
